import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ComboboxItem } from './Combobox';

interface Props<T extends string> {
  values: T[];
  onChange: (next: T[]) => void;
  items: ComboboxItem<T>[];
  placeholder?: string;
  searchPlaceholder?: string;
  /** Text shown in the trigger when one is selected — defaults to `"X selected"`. */
  selectedSummary?: (count: number) => string;
  disabled?: boolean;
  className?: string;
  align?: 'left' | 'right';
  /** Hide the search input even when items.length > 6. */
  hideSearch?: boolean;
}

/**
 * Multi-select sibling of `Combobox`. Same search + a11y semantics,
 * but each row toggles a checkmark instead of closing the popover.
 * Selected items render as removable chips inside the trigger so the
 * user can see and unpick them without reopening the menu.
 *
 * Used by onboarding's "Other countries you employ in" picker — the
 * previous hardcoded 13-checkbox grid didn't scale beyond a screenful.
 */
export function MultiCombobox<T extends string>({
  values, onChange, items, placeholder, searchPlaceholder, selectedSummary,
  disabled, className, align = 'left', hideSearch,
}: Props<T>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const valueSet = useMemo(() => new Set<string>(values), [values]);
  const selectedItems = useMemo(
    () => items.filter((i) => valueSet.has(i.value)),
    [items, valueSet],
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase().trim();
    return items.filter((i) => {
      const hay = [i.label, i.hint ?? '', i.value, ...(i.searchAliases ?? [])].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [items, query]);

  useEffect(() => {
    if (!open) return;
    setActiveIdx(0);
    setTimeout(() => searchRef.current?.focus(), 30);
    const onDown = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, filtered.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
      if (e.key === 'Enter') {
        e.preventDefault();
        const it = filtered[activeIdx];
        if (it) toggle(it.value);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filtered, activeIdx]);

  useEffect(() => {
    const el = listRef.current?.children[activeIdx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  function toggle(v: T) {
    if (valueSet.has(v)) onChange(values.filter((x) => x !== v));
    else onChange([...values, v]);
  }

  function remove(v: T) {
    onChange(values.filter((x) => x !== v));
  }

  const showSearch = !hideSearch && items.length > 6;
  const summary = selectedSummary
    ? selectedSummary(selectedItems.length)
    : t('common.nSelected', { count: selectedItems.length, defaultValue: `${selectedItems.length} selected` });

  return (
    <div className={`relative ${className ?? ''}`} ref={popoverRef}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="w-full min-h-10 inline-flex flex-wrap items-center gap-1.5 rounded-xl border border-border bg-card px-3 py-1.5 text-sm hover:bg-muted focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition disabled:opacity-60 disabled:pointer-events-none"
      >
        {selectedItems.length === 0 ? (
          <span className="truncate text-muted-foreground py-1">{placeholder ?? t('common.selectMany', { defaultValue: 'Select…' })}</span>
        ) : (
          <>
            {selectedItems.slice(0, 8).map((it) => (
              <span
                key={it.value}
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); remove(it.value); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); remove(it.value); }
                }}
                className="inline-flex items-center gap-1 rounded-md bg-primary/10 text-primary px-2 py-0.5 text-xs cursor-pointer hover:bg-primary/15"
              >
                {it.icon && <span className="shrink-0">{it.icon}</span>}
                <span className="truncate max-w-[120px]">{it.label}</span>
                <X className="h-3 w-3 opacity-70" />
              </span>
            ))}
            {selectedItems.length > 8 && (
              <span className="text-xs text-muted-foreground px-1">+{selectedItems.length - 8}</span>
            )}
            <span className="ms-auto text-[10px] text-muted-foreground shrink-0">{summary}</span>
          </>
        )}
        <ChevronDown className="h-4 w-4 opacity-60 shrink-0 ms-auto" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="listbox"
            aria-multiselectable="true"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className={`absolute z-50 top-[calc(100%+4px)] w-full min-w-[320px] max-h-96 rounded-2xl glass-strong shadow-card-lift overflow-hidden flex flex-col ${align === 'right' ? 'end-0' : 'start-0'}`}
          >
            {showSearch && (
              <div className="p-2 border-b border-border">
                <div className="relative">
                  <Search className="pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <input
                    ref={searchRef}
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
                    placeholder={searchPlaceholder ?? t('common.searchEllipsis', { defaultValue: 'Search…' })}
                    className="w-full h-8 rounded-lg border border-border bg-background ps-8 pe-2 text-xs outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                </div>
              </div>
            )}
            {filtered.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-muted-foreground">
                {t('common.noResults', { defaultValue: 'No matches' })}
              </p>
            ) : (
              <ul ref={listRef} className="overflow-y-auto keenez-scrollbar py-1">
                {filtered.map((it, i) => {
                  const checked = valueSet.has(it.value);
                  const focused = i === activeIdx;
                  return (
                    <li key={it.value}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={checked}
                        onMouseEnter={() => setActiveIdx(i)}
                        onClick={() => toggle(it.value)}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-start transition ${
                          checked ? 'bg-primary/5 text-foreground' :
                          focused ? 'bg-muted text-foreground' :
                          'text-foreground/85'
                        }`}
                      >
                        <span
                          className={`h-4 w-4 shrink-0 rounded border flex items-center justify-center transition ${
                            checked ? 'bg-primary border-primary text-white' : 'border-border bg-background'
                          }`}
                          aria-hidden
                        >
                          {checked && <Check className="h-3 w-3" />}
                        </span>
                        {it.icon && <span className="text-base shrink-0">{it.icon}</span>}
                        <span className="flex-1 min-w-0">
                          <span className={`block truncate ${checked ? 'font-medium' : ''}`}>{it.label}</span>
                          {it.hint && <span className="block text-[10px] text-muted-foreground truncate">{it.hint}</span>}
                        </span>
                        {it.badge && <span className="text-[10px] text-muted-foreground shrink-0">{it.badge}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
